import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import {
    useProductProfileLoadQuery,
    useProductProfileProductsLoadQuery,
    useProductProfileUpdateMutation,
} from "../../services/server/ProductProfileApi";
import type { ProductProfileEntity } from "../../types/ProductProfileEntity";

export const ProductProfileEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.PRODUCT_PROFILES}`);
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("product-profile");

    const [formData, setFormData] = useState<ProductProfileEntity>({} as ProductProfileEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useProductProfileLoadQuery({ entityId: id });
    const { data: fetchedProducts, isFetching: productsAreFetching } = useProductProfileProductsLoadQuery(
        id ? { productProfileId: id } : undefined,
    );

    useEffect(() => {
        if (loadFormData) {
            setFormData({
                ...loadFormData,
                products: loadFormData.products?.length ? loadFormData.products : (fetchedProducts ?? []),
            });
        }
    }, [loadFormData, fetchedProducts]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useProductProfileUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "ProductProfile" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress || productsAreFetching}>
            {formDefinition?.formConfiguration && formData?.id && (
                <FormManager<ProductProfileEntity>
                    name="ProductProfileEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
